<template>
  <div class="content bg8">
      <div class="middled">
        <div class="basehero">
          <div class="tophero fpghero">
            <h1>Founder Peer Groups</h1>
            <h2 class="section">Get the right support to bootstrap, launch, and grow a valuable startup faster by joining a Founder Peer Group with serious founders like you.</h2>
            <div class="button-row">
              <a href="https://www.calendly.com/foclarity/30min" target="_blank" rel="nofollow">
                <button class="wide" style="background: #ffaa00; color: #000;border: 1px solid #fff;">Apply Now</button>
              </a>
            </div>
          </div>

          <p>
            At Foclarity, we believe the fastest way to grow as a founder is to learn with other founders who are facing, and overcoming, the same challenges. 
            Our peer groups are small, confidential circles where real founders share what’s working, what isn’t, and what’s next... 
            hosted by Mash (Michael Ashley) so you get valuable conversations rooted in real-world experience.
          </p>
          <p>
            You bring your experience and questions; your peers bring theirs. Together, you get focus, clarity, and accountability.
          </p>

          <h3>Who This Is For</h3>
          <ul>
            <li>Founders bootstrapping startups with some traction</li>
            <li>People who value honesty, humility, and practical execution</li>
            <li>Leaders who want consistent progress, not startup theater</li>
          </ul>

          <h3>Group Tracks</h3>
          <p>We match founders into focused tracks so conversations stay relevant and useful.</p>
          <ul>
            <li><strong>Launch:</strong> Turning early traction into repeatable results.</li>
            <li><strong>Growth:</strong> Hiring your first key roles, refining pricing, and building a simple, repeatable go-to-market.</li>
            <li><strong>Scale:</strong> Leading through complexity, systems, and teams while protecting what makes the product great.</li>
          </ul>

          <h3>What You Get</h3>
          <ul>
            <li><strong>Curated circle:</strong> 6–8 founders at a similar stage with complementary experience.</li>
            <li><strong>Structured sessions:</strong> Monthly 3-hour zoom meetings with your peers, hosted by Mash.</li>
            <li><strong>Operator playbooks:</strong> Practical frameworks for hiring, pricing, onboarding, retention, and founder-led sales.</li>
            <li><strong>Signal over noise:</strong> A private space to speak plainly about numbers, people, and decisions.</li>
          </ul>

          <h3>How It Works</h3>
          <ol>
            <li><strong>Apply:</strong> Tell us about your company, stage, and goals.</li>
            <li><strong>Interview:</strong> We confirm fit and recommend a track.</li>
            <li><strong>Match:</strong> You’re placed into a curated peer circle.</li>
            <li><strong>Kickoff:</strong> Set shared norms, goals, and a simple scorecard.</li>
            <li><strong>Meet:</strong> Monthly sessions, with lightweight periodic check-ins.</li>
          </ol>

          <h3>Why Foclarity</h3>
          <ul>
            <li><strong>Founder-first:</strong> Built for people doing the work, not just talking about it.</li>
            <li><strong>Practical and real:</strong> We focus on actions that move the business forward.</li>
            <li><strong>Confidential:</strong> What’s shared in the circle stays in the circle.</li>
          </ul>

          <h3>Time and Investment</h3>
          <ul>
            <li><strong>Cadence:</strong> One 3 hour meeting per month.</li>
            <li><strong>Membership:</strong> Annual commitment. Pricing starts at $6,000 per year.</li>
          </ul>

          <h3>Optional On-Ramp</h3>
          <p>
            If you’re pre-traction and want help getting launch-ready, we occasionally run a short Startup Launch Bootcamp. 
            Graduates who gain traction can apply to join a Foclarity Peer Group.
            Ask us about it why you apply.
          </p>

          <h3>What Founders Say</h3>
          <blockquote>
            “Before Foclarity, we were swimming in an endless sea of great ideas and possibilities, 
            but we had no anchor to tether them to. 
            Foclarity worked with us to clarify our offerings, 
            focus on strategic steps forward and ensure that our vision was aligned—both as an executive team and brand. 
            The platform and people behind it have played a pivotal role in Vault's evolution. 
            The team is experienced, insightful and genuinely dedicated to our success. 
            Thank you for helping us find the focus and clarity we needed to succeed!”
            <br>— Allison Beck<br>
            Co-founder | Vault
          </blockquote>

          <h3>Ready to Join?</h3>
          <p>
            If you’re a founder who wants honest peers, practical help, and steady progress, we’d love to hear from you.
          </p>
          <div class="button-row">
            <a href="https://www.calendly.com/foclarity/30min" target="_blank" rel="nofollow">
              <button class="wide" style="background: #2E5077; color: #fff;">Apply Now</button>
            </a>
          </div>

          <hr>

          <h3>Meet Mash, Your Host</h3>
          <img src="@/assets/mash-michael-ashley.png" alt="Mash Michael Ashley, MBA" style="float:left;margin:0px 15px 0px 0;border-radius:50%;width:140px;height:140px;border:1px solid #ccc;">
          <p>
            Michael “Mash” Ashley is the Founder and CEO of Foclarity. With over 25 years at the intersection of innovation, strategy, and execution, Mash has co-founded and scaled startups—including four successful exits—guided dozens of companies from concept to traction, and launched a university-based incubator at San José State University.
          </p>
          <p>
            As an Adjunct Professor at San José State’s College of Business, Mash has taught and mentored hundreds of aspiring founders, blending deep entrepreneurial experience with academic rigor. His passion is helping founders cut through confusion, focus on what matters, and take bold ideas into the real world.
          </p>
          <p>
            Today, Mash brings that same expertise to Foclarity Peer Groups. As your host, he creates a structured, supportive environment where founders can speak openly, learn from each other, and leave every session with clarity and next steps. Mash knows the founder journey firsthand—the wins, the losses, and the grit required to keep going—and he ensures that every group conversation is valuable, practical, and rooted in real-world experience.
          </p>
          <p>
            LinkedIn: <a href="https://www.linkedin.com/in/mashley" target="_blank">Michael (Mash) Ashley</a>
          </p>

        </div>
      </div>
    </div>
</template>

<script>
export default {
  title: "Founder Peer Groups for Early-Stage CEOs | Foclarity",
  description: 'Join founder peer groups designed to help early-stage CEOs and startup leaders get perspective, support, and clearer next steps.',
  name: "FounderPeerGroups",
  methods: {
    onProgramChange(event) {
      const path = event.target.value;
      if (path && path !== this.$route.path) {
        this.$router.push(path);
      }
    }
  }
};
</script>

<style scoped>
.subnav-link {
  margin: 0 10px;
  color: #2E5077;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
}
.subnav-link:hover {
  color: #7a4fa3;
  text-decoration: underline;
}
.subnav-link.router-link-exact-active {
  text-decoration: underline;
  font-weight: bold;
}
.button-row {
  display: flex;
  gap: 16px;
  justify-content: center;
  align-items: center;
  margin-bottom: 1em;
}
.button-row a {
  display: inline-block;
  @media only screen and (max-width: 440px) {
    width: 100%;
  }
}
.explore-programs-dropdown {
  margin: 40px auto 0 auto;
  max-width: 600px;
  text-align: center;
  padding: 24px 16px 18px 16px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px 0 rgba(191,0,255,0.04);
}
.dropdown-label {
  font-weight: 600;
  color: #7a4fa3;
  font-size: 1.1rem;
  margin-bottom: 10px;
  display: block;
}
.program-select {
  width: 100%;
  max-width: 420px;
  padding: 12px 16px;
  font-size: 1.08rem;
  border: 1.5px solid #2E5077;
  border-radius: 8px;
  background: #f7f2fa;
  color: #41037b;
  font-weight: 500;
  outline: none;
  transition: border 0.2s;
  margin: 0 auto;
  box-shadow: 0 1px 4px 0 rgba(122,79,163,0.04);
}
.program-select:focus {
  border: 2px solid #7a4fa3;
}
@media (max-width: 600px) {
  .button-row {
    flex-direction: column;
    gap: 8px;
  }
  .explore-programs-dropdown {
    padding: 16px 4px 12px 4px;
  }
  .program-select {
    font-size: 1em;
    padding: 10px 8px;
    max-width: 100%;
  }
}
.fpghero {
  background: linear-gradient(rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.8)),  url("~@/assets/peer-group-session.jpg")no-repeat top right ;
  background-size: cover;
  min-height: 800px;
  align-content: end;
  @media only screen and (max-width: 440px) {
  background: linear-gradient(rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.8)),  url("~@/assets/peer-group-session.jpg")no-repeat top right ;
    background-position-y: 0px;
    background-position-x: -540px;
    background-size: 330%;
  }
}
</style>
